import { useState, useMemo, useEffect } from "react";
import { useCookies } from "react-cookie";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { WarehouseService } from "../../../api/warehouse.service";
import { allWarehouseHeights } from "../../../constants";
import { ResultEntry, LocationData } from "../../../types/data";
import { validateEan } from "../../../utils";

export const useManagePageLogic = () => {
  const queryClient = useQueryClient();
  const [cookie] = useCookies(["access_key"]);

  const [selectedShelf, setSelectedShelf] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [eanField, setEanField] = useState("");
  const [quantityField, setQuantityField] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [enabledConfirmButton, setEnabledConfirmButton] = useState(false);
  const [newData, setNewData] = useState<ResultEntry[]>([]);

  const [bufferedData, setBufferedData] = useState<LocationData | null>(null);
  const [isMinimumBlurActive, setIsMinimumBlurActive] = useState(false);

  const [isSlowConnection, setIsSlowConnection] = useState(false);

  const groups = useMemo(() => [...new Set(allWarehouseHeights.map((s) => s.shelf.slice(0, 4)))].sort(), []);
  const currentShelfData = allWarehouseHeights.find((s) => s.shelf === selectedShelf);

  const [autoFill, setAutoFill] = useState<boolean>(() => {
    const saved = localStorage.getItem("autofill");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isInternalChange, setIsInternalChange] = useState(false);

  const palletQuery = useQuery<LocationData>({
    queryKey: ["pallet", selectedLocation],
    queryFn: () => WarehouseService.fetchPalletData(selectedLocation, cookie.access_key),
    enabled: !!selectedLocation,
    retry: false,
    staleTime: 0,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false
  });

  const [overwriteEANquantity, setOverwriteEANquantity] = useState<boolean>(() => {
    const saved = localStorage.getItem("overwriteQuantities");
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem("autofill", JSON.stringify(autoFill));
    localStorage.setItem("overwriteQuantities", JSON.stringify(overwriteEANquantity));
  }, [autoFill, overwriteEANquantity]);

  useEffect(() => {
    if (palletQuery.isError) {
      setIsMinimumBlurActive(false);
      setIsSlowConnection(false);
      setBufferedData(null);
      return;
    }

    let timer: ReturnType<typeof setTimeout>;
    let slowTimer: ReturnType<typeof setTimeout>;

    if (palletQuery.isFetching) {
      setIsMinimumBlurActive(true);
      slowTimer = setTimeout(() => setIsSlowConnection(true), 3000);
    } else if (palletQuery.data && !palletQuery.isFetching) {
      timer = setTimeout(() => {
        if (palletQuery.data?.location === selectedLocation) {
          setBufferedData(palletQuery.data);
          setNewData(autoFill ? palletQuery.data.results || [] : []);
          setEanField("");
          setQuantityField("");
          setIsMinimumBlurActive(false);
          setIsSlowConnection(false);
        }
      }, 500);
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (slowTimer) clearTimeout(slowTimer);
    };
  }, [palletQuery.isFetching, palletQuery.data, palletQuery.isError, selectedLocation, autoFill]);

  useEffect(() => {
    if (!bufferedData || palletQuery.isFetching) return;
    if (isInternalChange) {
      setNewData(autoFill ? bufferedData.results || [] : []);
      setIsInternalChange(false);
    }
  }, [autoFill, bufferedData, palletQuery.isFetching, isInternalChange]);

  useEffect(() => {
    if (!selectedLocation) {
      setBufferedData(null);
      setIsMinimumBlurActive(false);
      setIsSlowConnection(false);
    }
  }, [selectedLocation]);

  const saveMutation = useMutation({
    mutationFn: (results: ResultEntry[]) =>
      WarehouseService.savePalletData(selectedLocation, results, cookie.access_key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pallet", selectedLocation] });
      setErrorMessage("");
      setEnabledConfirmButton(false);
    },
    onError: (error: any) => {
      setErrorMessage(error.response?.data?.message || "Błąd zapisu danych.");
      setEnabledConfirmButton(false);
    }
  });

  useEffect(() => {
    setEnabledConfirmButton(false);
  }, [newData]);

  const isSame = useMemo(() => {
    if (!bufferedData) return true;
    const dbResults = bufferedData.results || [];
    if (dbResults.length !== newData.length) return false;
    return newData.every((newItem) => {
      const dbItem = dbResults.find((d) => d.ean === newItem.ean);
      return dbItem && dbItem.quantity === newItem.quantity;
    });
  }, [bufferedData, newData]);

  const actions = {
    selectShelf: (shelf: string) => {
      setSelectedShelf(shelf);
      setSelectedLocation("");
      setNewData([]);
      setBufferedData(null);
    },
    selectLocation: (location: string) => {
      setSelectedLocation(location);
      setEnabledConfirmButton(false);
      setErrorMessage("");
    },
    addEan: (ean: string, quantity: string) => {
      if (palletQuery.isError || !bufferedData) {
        setErrorMessage("Brak połączenia z serwerem.");
        return false;
      }
      setEnabledConfirmButton(false);
      const cleanEan = ean.trim();
      if (!validateEan(cleanEan)) {
        setErrorMessage("EAN musi składać się z od 4 do 13 cyfr.");
        return false;
      }
      const numQty = Number(quantity);
      if (isNaN(numQty) || numQty < 0) {
        setErrorMessage("Błędna ilość.");
        return false;
      }
      setNewData((prev) => {
        const existingIndex = prev.findIndex((item) => item.ean === cleanEan);
        if (existingIndex !== -1 && !overwriteEANquantity) {
          const updatedData = [...prev];
          updatedData[existingIndex] = {
            ...updatedData[existingIndex],
            quantity: updatedData[existingIndex].quantity + numQty
          };
          return updatedData;
        } else {
          const filtered = prev.filter((item) => item.ean !== cleanEan);
          return [...filtered, { ean: cleanEan, quantity: numQty }];
        }
      });
      setEanField("");
      setQuantityField("");
      setErrorMessage("");
      return true;
    },
    removeEan: (ean: string) => {
      setNewData((prev) => prev.filter((i) => i.ean !== ean));
      setErrorMessage("");
    },
    saveChanges: () => {
      if (isSame || !selectedLocation || palletQuery.isError) return;
      if (!enabledConfirmButton) {
        setEnabledConfirmButton(true);
        return;
      }
      saveMutation.mutate(newData);
    },
    selectNextShelf: (direction: number) => {
      const i = allWarehouseHeights.findIndex((s) => s.shelf === selectedShelf);
      let nextIdx = (i + direction + allWarehouseHeights.length) % allWarehouseHeights.length;
      actions.selectShelf(allWarehouseHeights[nextIdx].shelf);
    },
    toggleAutoFill: () => {
      setAutoFill((prev) => !prev);
      setIsInternalChange(true);
    },
    toggleOverwriteQuantities: () => {
      setOverwriteEANquantity((prev) => !prev);
      // setIsInternalChange(true);
    }
  };

  return {
    data: {
      databaseData: bufferedData,
      newData,
      isSame,
      selectedShelf,
      selectedLocation,
      currentShelfData,
      groups,
      showSkeleton: !!selectedLocation && bufferedData === null && (palletQuery.isFetching || isMinimumBlurActive),
      isSyncing: (palletQuery.isFetching || isMinimumBlurActive || saveMutation.isPending) && !!bufferedData,
      isSlowConnection,
      autoFill,
      overwriteEANquantity
    },
    ui: {
      errorMessage:
        errorMessage ||
        (palletQuery.isError
          ? (palletQuery.error as any)?.response?.data?.message || "Brak połączenia z serwerem."
          : ""),
      enabledConfirmButton,
      eanField,
      quantityField
    },
    actions: { ...actions, setEanField, setQuantityField, setOverwriteEANquantity }
  };
};
